import React, { useState, useContext, useEffect } from "react";
import PropTypes from "prop-types";
import moment from "moment";
import gql from "graphql-tag";

import { useMutation, useQuery, useApolloClient } from "react-apollo";
import { Link } from "react-router-dom";
import { Field, Control, Title, Button, Icon } from "rbx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { toast } from "react-toastify";
import { UPDATE_ORDER_MUTATION } from "../../../../graphql/Orders/Orders.graphql";

import { ALL_ROUTE_PRODUCTS_QUERY } from "../../../../graphql/Routes/RouteProducts.graphql";

import { ModalContext } from "../../../../components/ModalProvider";

import DataTable from "../../../../components/DataTable";
import CancelledInfo from "./CancelledInfo";
import BillingRouteCompareModal from "./BilledRouteRefreshModal";

import {
  ORDER_DETAIL_BILLING_COLUMNS,
  ORDER_DETAIL_DISPATCHER_COLUMNS
} from "./columns";

import {
  AddOrderDetailForm,
  EditOrderDetailForm
} from "../../../../components/Orders";

import "./OrderDetails.scss";

import { Confirmation } from "../../../../components/modals";
import { formatCurrency, getValueFromObject, equals } from "../../../../utils";
import { withOutsideDateRange } from "./DeliveryDate";
import Loading from "../../../../components/Loading";

const SINGLE_ORDER_QUERY = gql`
  query Order($where: OrderWhereUniqueInput!) {
    Order(where: $where) {
      RecId
      ScheduledDeliveryDate
      NonBill
      InvoiceNumber
      InvoiceDate
      InvoiceBatchNumber
      OrderNumber
      LoadType {
        RecId
        CodeDescription
      }
      PaymentTerm {
        RecId
        TermDescription
      }
      BillTo {
        RecId
        CustomerNo
        Name
        City
        State
      }
      Route {
        RecId
        Contract
        Tolls
        ChargeForTolls
        TariffRecIdRef
        RouteId
        RouteDescription
        BillTo {
          RecId
          CustomerNo
          Name
          City
          State
        }
        NonBill
        PaymentTerm {
          RecId
          TermDescription
        }
        LoadType {
          RecId
          CodeDescription
        }
        Tariff {
          RecId
          Description
          TariffCode
          TariffType {
            RecId
            CodeDescription
          }
          FuelSurchargeRate
          BeginDate
          EndDate
          MinCharge
          MinLoad
          MaxLoad
        }
      }
      Tariff {
        RecId
        Description
        TariffCode
        TariffType {
          RecId
          CodeDescription
        }
        FuelSurchargeRate
        BeginDate
        EndDate
        MinCharge
        MinLoad
        MaxLoad
      }
    }
  }
`;

const PERFORM_ORDER_LINE_DETAILS_CALCULATION = gql`
  mutation performOrderLineDetailCalculation(
    $orderRecId: Int!
    $override: Boolean
  ) {
    performOrderLineDetailCalculation(
      orderRecId: $orderRecId
      override: $override
    ) {
      RecId
      Description
      Quantity
      Rate
      Extension
      ExternalComm
      InternalComm
      Hazmat
      ProductRecIdRef
      TariffRecIdRef
      UOMRecIdRef
      Percentage
      Note
      OrderRecIdRef
      Percentage
      OverrideFlag
      Product {
        RecId
        ProductCode
        ProductCategory {
          RecId
          CodeDescription
        }
      }
      UnitOfMeasurement {
        RecId
        CodeDescription
      }
    }
  }
`;

export const ALL_ORDER_DETAILS_QUERY = gql`
  query OrderDetails(
    $where: OrderDetailWhereInput!
    $orderBy: OrderDetailOrderByInput
  ) {
    OrderDetails(where: $where, orderBy: $orderBy, orderByMulti: [RecId_DESC]) {
      RecId
      Description
      Quantity
      Rate
      Extension
      ExternalComm
      InternalComm
      Hazmat
      ProductRecIdRef
      TariffRecIdRef
      UOMRecIdRef
      Percentage
      Note
      OrderRecIdRef
      Percentage
      OverrideFlag
      Product {
        RecId
        ProductCode
        ListOrder
        ProductCategory {
          RecId
          CodeDescription
        }
        CalcType {
          RecId
          ListOrder
        }
      }
      UnitOfMeasurement {
        RecId
        CodeDescription
      }
    }
  }
`;

const FIND_FIRST_PRODUCT_QUERY = gql`
  query FIND_FIRST_PRODUCT_QUERY($where: ProductWhereInput) {
    Products(where: $where, first: 1) {
      RecId
      ProductCode
      ProductCategory {
        RecId
        CodeDescription
      }
      CalcType {
        RecId
        ListOrder
      }
      UOMRecIdRef
      ProductUOM {
        RecId
        CodeDescription
      }
    }
  }
`;
const ALL_SYSTEM_CODES_QUERY = gql`
  query SystemCodes($where: SystemCodeWhereInput!) {
    SystemCodes(where: $where) {
      RecId
      Type
      Category
      CodeName
      CodeId
      CodeDescription
    }
  }
`;

const VerifyButton = withOutsideDateRange(
  ({
    isOutsideRange,
    isOutsideTariffRange,
    isOutsideRouteRange,
    name,
    handleClick
  }) => {
    useEffect(() => {
      if (isOutsideRouteRange) {
        toast.warning(
          "Delivery date is outside route's date range. This order cannot be verified."
        );
      }
      if (isOutsideTariffRange) {
        toast.warning(
          "Delivery date is outside tariff's date range. This order cannot be verified."
        );
      }
    }, [isOutsideRouteRange, isOutsideTariffRange]);

    return (
      <Button
        color="warning"
        disabled={isOutsideRange || isOutsideRouteRange || isOutsideTariffRange}
        size="small"
        onClick={handleClick}
      >
        <Icon>
          <FontAwesomeIcon icon="certificate" />
        </Icon>
        <span>{name}</span>
      </Button>
    );
  }
);

VerifyButton.propTypes = {
  isOutsideRange: PropTypes.bool,
  name: PropTypes.string,
  handleClick: PropTypes.func
};

const OrderDetails = ({ orderRecId, billable, statusRecId, isDispatcher }) => {
  const [calculateLoading, setCalculateLoading] = useState(false);
  const client = useApolloClient();
  const SystemCodes = useQuery(ALL_SYSTEM_CODES_QUERY, {
    variables: {
      where: {
        Category_in: ["Order"],
        CodeName_in: ["OrderStatus"],
        CodeId_in: ["BV", "RV", "NB", "EXA", "CAN", "INV", "BP", "IPA"]
      }
    }
  });
  const [hasCalculated, setHasCalculated] = useState(false);
  const { setModalOpen } = useContext(ModalContext);

  const [total, setTotal] = useState(null);
  const [States, setStates] = useState(null);

  const [updateOrder] = useMutation(UPDATE_ORDER_MUTATION, {
    refetchQueries: ["SINGLE_ORDER_QUERY", "ALL_ORDERS_VIEW_QUERY"]
  });

  const { data: OrderData, refetch } = useQuery(SINGLE_ORDER_QUERY, {
    variables: {
      where: {
        RecId: orderRecId
      }
    },
    fetchPolicy: "network-only"
  });

  const [calculate, { loading }] = useMutation(
    PERFORM_ORDER_LINE_DETAILS_CALCULATION,
    {
      variables: {
        orderRecId
      },
      refetchQueries: ["OrderDetails"]
    }
  );
  const handleAdd = () => {
    if (!isDispatcher) {
      setModalOpen(
        true,
        <AddOrderDetailForm
          OrderRecIdRef={orderRecId}
          isDispatcher={isDispatcher}
          setModalOpen={setModalOpen}
        />
      );
    }
  };

  const handleRowClick = data => {
    if (!isDispatcher) {
      setModalOpen(
        true,
        <EditOrderDetailForm
          isDispatcher={isDispatcher}
          orderDetail={data}
          setModalOpen={setModalOpen}
        />
      );
    }
  };

  const handleCalculate = async () => {
    setCalculateLoading(true);
    const { data: OrderDetailsData } = await client.query({
      query: ALL_ORDER_DETAILS_QUERY,
      variables: {
        where: { OrderRecIdRef: orderRecId },
        orderBy: "DESCRIPTION_ASC"
      },
      fetchPolicy: "network-only"
    });

    const { data: RouteProductDetailsData } = await client.query({
      query: ALL_ROUTE_PRODUCTS_QUERY,
      variables: {
        where: { RouteRecIdRef: parseInt(OrderData?.Order?.Route.RecId) },
        orderBy: "ProdDescription_ASC"
      },
      fetchPolicy: "network-only"
    });

    const order = OrderData?.Order || {};
    const route = OrderData?.Order?.Route || {};
    const orderDetails = OrderDetailsData?.OrderDetails || [];

    const routeProducts = RouteProductDetailsData?.RouteProducts
      ? [...RouteProductDetailsData.RouteProducts]
      : [];

    if (route.Tolls) {
      const { data: TollProductData } = await client.query({
        query: FIND_FIRST_PRODUCT_QUERY,
        variables: {
          where: { ProductCode: "TOLLS" }
        },
        fetchPolicy: "network-only"
      });

      const tollProduct = TollProductData?.Products?.[0] || {
        RecId: 30,
        ProductCode: "TOLLS",
        ProductCategory: {
          RecId: 307,
          CodeDescription: "Fee"
        },
        ProductUOM: {
          CodeDescription: "Flat Fee",
          RecId: 303
        },
        UOMRecIdRef: 303,
        CalcType: { RecId: 341, ListOrder: 2 }
      };

      routeProducts.push({
        ProdDescription: "Tolls",
        Rate: route.ChargeForTolls,
        Product: tollProduct,
        UOM: tollProduct?.ProductUOM,
        UOMRecIdRef: tollProduct?.UOMRecIdRef || 303
      });
    }
    if (route.Tariff) {
      const fuelSurchargeRouteProductIdx = routeProducts.findIndex(
        rp => rp?.Product?.ProductCode === "FUELSC"
      );
      if (fuelSurchargeRouteProductIdx > -1) {
        routeProducts[fuelSurchargeRouteProductIdx].Percentage =
          route.Tariff.FuelSurchargeRate;
      }
    }

    const routeHasUpdates = [
      "BillTo.RecId",
      "LoadType.CodeDescription",
      "PaymentTerm.TermDescription",
      "Tariff.Description"
    ].some(field => {
      const orderValue = getValueFromObject(order, field);
      const routeValue = getValueFromObject(route, field);

      return !equals(orderValue, routeValue);
    });

    const allRouteProductsIncludedInOrderDetails = routeProducts.every(
      detail => {
        if (detail.Product?.ProductCode === "FUELSC") {
          return orderDetails.some(
            oProduct =>
              oProduct.ProductRecIdRef === detail.Product?.RecId &&
              oProduct.Percentage === detail.Percentage
          );
        }
        return orderDetails.some(
          oProduct =>
            oProduct.Product.RecId === detail.Product.RecId &&
            oProduct.Rate === detail.Rate &&
            oProduct.UOMRecIdRef === detail.UOMRecIdRef
        );
      }
    );

    if (routeHasUpdates || !allRouteProductsIncludedInOrderDetails) {
      setModalOpen(
        true,
        <BillingRouteCompareModal
          order={order}
          orderDetails={orderDetails}
          route={route}
          routeProducts={routeProducts}
          onCancel={() => setModalOpen(false)}
          onComplete={async () => {
            setHasCalculated(true);
            await calculate({
              variables: {
                orderRecId,
                override: false
              }
            });
            refetch();
            setModalOpen(false);
          }}
          onCompleteWithoutSync={async () => {
            setHasCalculated(true);
            await calculate({
              variables: {
                orderRecId,
                override: true
              }
            });
            refetch();
            setModalOpen(false);
          }}
        />
      );
    } else {
      setHasCalculated(true);
      await calculate({
        variables: {
          orderRecId,
          override: false
        }
      });
    }
    setCalculateLoading(false);
  };

  const asyncUpdateOrder = async updates => {
    await updateOrder({
      variables: {
        data: {
          ...updates
        },
        where: {
          RecId: orderRecId
        }
      }
    });
  };
  const handleVerify = async () => {
    if (statusRecId === States.Cancelled && total === 0) {
      setModalOpen(
        true,
        <Confirmation
          message="There are no charges on this order. Would you like to set the status to No-Bill?"
          onConfirm={() => {
            asyncUpdateOrder({
              StatusRecIdRef: States.NonBillable.RecId,
              NonBill: true
            });
            setModalOpen(false, "");
          }}
        />
      );
    } else if (OrderData.Order.NonBill) {
      asyncUpdateOrder({ StatusRecIdRef: States.NonBillable.RecId });
    } else {
      await asyncUpdateOrder({ StatusRecIdRef: States.BillingVerified.RecId });
    }
  };

  // disable verify button if delivery date is outside tariff or route date range

  const where = isDispatcher
    ? { OrderRecIdRef: orderRecId, ProdCatgRecIdRef: 306 }
    : { OrderRecIdRef: orderRecId };

  const { data: OrderDetailData } = useQuery(ALL_ORDER_DETAILS_QUERY, {
    variables: { where }
  });

  useEffect(() => {
    if (OrderDetailData && Array.isArray(OrderDetailData.OrderDetails)) {
      const sum = OrderDetailData.OrderDetails.reduce(
        (a, c) => a + c.Extension,
        0
      );
      setTotal(sum);
    }
    if (SystemCodes?.data?.SystemCodes?.length > 0 && !States) {
      setStates({
        RouteVerified: SystemCodes.data.SystemCodes.find(
          s => s.CodeId === "RV"
        ),
        BillingVerified: SystemCodes.data.SystemCodes.find(
          s => s.CodeId === "BV"
        ),
        NonBillable: SystemCodes.data.SystemCodes.find(s => s.CodeId === "NB"),
        ExportedToAccounting: SystemCodes.data.SystemCodes.find(
          s => s.CodeId === "EXA"
        ),
        Cancelled: SystemCodes.data.SystemCodes.find(s => s.CodeId === "CAN"),
        ImportedInAccounting: SystemCodes.data.SystemCodes.find(
          s => s.CodeId === "IPA"
        ),
        InvoiceGenerated: SystemCodes.data.SystemCodes.find(
          s => s.CodeId === "INV"
        ),
        BilledAndPosted: SystemCodes.data.SystemCodes.find(
          s => s.CodeId === "BP"
        )
      });
    }
  }, [OrderDetailData, States, SystemCodes, SystemCodes.data]);

  if (SystemCodes?.loading || !States?.RouteVerified || !States?.Cancelled) {
    return <Loading />;
  }
  return (
    <div className="subpage">
      <div
        className="row details-info-row"
        style={{
          gridTemplateColumns:
            statusRecId === States.Cancelled?.RecId
              ? "0.8fr 1fr 1fr 1fr 1fr 1fr 0.5fr"
              : "0.8fr 1fr 1fr 1fr 1fr 0.5fr"
        }}
      >
        <Title
          subtitle
          size={5}
          style={{ margin: 0, paddingRight: 40, marginTop: "0.2rem" }}
        >
          Order Details
        </Title>
        {!isDispatcher && typeof total === "number" && (
          <React.Fragment>
            <Button static>
              <div style={{ fontSize: 16, fontWeight: "bolder" }}>
                <b>Total:</b>&nbsp;<em>{formatCurrency(total)}</em>
              </div>
            </Button>
            {statusRecId === States.InvoiceGenerated.RecId ||
            statusRecId === States.ExportedToAccounting.RecId ||
            statusRecId === States.ImportedInAccounting.RecId ||
            statusRecId === States.BilledAndPosted.RecId ? (
              <React.Fragment>
                <Button static style={{ padding: 0 }}>
                  <div style={{ padding: 10 }}>
                    <b>Invoice #: {OrderData?.Order?.InvoiceNumber}</b>
                  </div>
                </Button>
                <Button static style={{ padding: 0 }}>
                  <div style={{ padding: 10 }}>
                    <b>
                      Invoice Date:{" "}
                      {moment(OrderData?.Order?.InvoiceDate).format(
                        "MM/DD/YYYY"
                      )}
                    </b>
                  </div>
                </Button>
                <Button static style={{ padding: 0 }}>
                  <div style={{ padding: 10 }}>
                    <b>
                      Invoice Batch #: {OrderData?.Order?.InvoiceBatchNumber}
                    </b>
                  </div>
                </Button>
              </React.Fragment>
            ) : (
              <React.Fragment>
                <span />
                <span />
                <span />
              </React.Fragment>
            )}
            {statusRecId === States.Cancelled?.RecId && (
              <div className="od-page-head-middle">
                <CancelledInfo orderRecId={orderRecId} />
              </div>
            )}
          </React.Fragment>
        )}
        <Field kind="group" style={{ marginTop: "0.2rem" }}>
          {!isDispatcher && (
            <Control>
              <Button
                as={Link}
                color="primary"
                size="small"
                tabIndex="-1"
                to="/finance/invoice-processing/route-verified"
              >
                <Icon>
                  <FontAwesomeIcon icon="arrow-left" />
                </Icon>
                <span>Invoice Processing</span>
              </Button>
            </Control>
          )}
          {((hasCalculated && statusRecId === States.RouteVerified.RecId) ||
            statusRecId === States.Cancelled.RecId) && (
            <Control>
              <VerifyButton
                handleClick={handleVerify}
                name={billable ? "Verify" : "Non-Billable"}
                routeRecId={OrderData?.Order?.Route?.RecId}
                tariffRecId={OrderData?.Order?.Route?.TariffRecIdRef}
                value={OrderData?.Order?.ScheduledDeliveryDate}
              />
            </Control>
          )}
          {!isDispatcher &&
            [States.RouteVerified.RecId, States.BillingVerified.RecId].includes(
              statusRecId
            ) && (
              <Control>
                <Button
                  color="link"
                  size="small"
                  state={loading || calculateLoading ? "loading" : ""}
                  onClick={handleCalculate}
                >
                  <Icon>
                    <FontAwesomeIcon icon="calculator" />
                  </Icon>
                  <span>Calculate</span>
                </Button>
              </Control>
            )}
          {!isDispatcher && (
            <Control>
              <Button
                color="success"
                disabled={[
                  States.ExportedToAccounting.RecId,
                  States.NonBillable.RecId
                ].includes(statusRecId)}
                size="small"
                onClick={handleAdd}
              >
                <Icon>
                  <FontAwesomeIcon icon="plus" />
                </Icon>
                <span>Add Order Detail</span>
              </Button>
            </Control>
          )}
        </Field>
      </div>
      <DataTable
        columns={
          isDispatcher
            ? ORDER_DETAIL_DISPATCHER_COLUMNS
            : ORDER_DETAIL_BILLING_COLUMNS
        }
        dataQuery={ALL_ORDER_DETAILS_QUERY}
        fetchPolicy="cache-and-network"
        initialSortBy={[
          {
            id: "Product.ListOrder",
            desc: false
          },
          {
            id: "Description",
            desc: false
          }
        ]}
        name="OrderDetail"
        where={where}
        onRowClick={handleRowClick}
      />
    </div>
  );
};

OrderDetails.propTypes = {
  orderRecId: PropTypes.number,
  isDispatcher: PropTypes.bool,
  billable: PropTypes.bool,
  statusRecId: PropTypes.number
};
export default OrderDetails;
